import noop from 'lodash/noop';

import { MetaGamesEventType } from '../../../../dto/metagames.types';
import { useEventTriggerWidgets } from '../../../../features/widgets/hooks/useEventTriggerWidgets';
import type { EventTriggerWidgetType, WidgetEntry } from '../../../../features/widgets/types';
import { useStartGame } from '../../../../hooks/useStartGame';
import { useFlag } from '../../../../services/launchDarkly';
import { EnumOpenDialog } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';

import { resolveBannerCtaAction } from './bannerCtaAction';
import {
  isChaseAvailable,
  isTournamentOpenForPlayer,
  selectStoreMetagameCtaAvailability,
} from './bannerCtaAvailability';
import { useBannerPhoneGate } from './useBannerPhoneGate';

export const useBannerCta = () => {
  const {
    uiStore,
    rewardsStore,
    hammersStore,
    trailStore,
    puzzleStore,
    sportEventsStore,
    jackpotStore,
    gameUiStore,
    eventTriggerStore,
    tournamentStore,
    tournamentOfChampionsStore,
    coinTypeStore,
    metagamesStore,
    questStore,
    cardWarStore,
  } = useStores();
  const { handleStartGame } = useStartGame();
  const startWithPhoneGate = useBannerPhoneGate();
  // Read during render (callers are observers) so a coin toggle re-resolves the CTA target / on-off state.
  const { coinType } = coinTypeStore;
  const inviteFriendsEnabled = useFlag<boolean>('invite_friends_enabled', true);
  const isCrownSportsEnabled = useFlag('crown_sports_feature', false);
  const monetizationEventsEnabled = useFlag('monetization_events_enabled', true);
  const metagamesEnabled = useFlag('metagames_enabled', true);
  // useEventTriggerWidgets only ever emits these 4 widget types; the cast documents that instead
  // of re-deriving its offers_feat/messages_feat/road_feature_web gating here.
  const eventTriggerWidgets = useEventTriggerWidgets() as WidgetEntry<EventTriggerWidgetType>[];

  // Reads the stores on each call: once during render, again after the tap's fetch.
  const getTournamentLeaderboardAvailable = () =>
    isTournamentOpenForPlayer({
      currentTournament: tournamentStore.currentTournament,
      coinType: coinTypeStore.coinType,
      tournamentOfChampions: tournamentOfChampionsStore.tournamentOfChampions,
      shouldRenderTocDialog: tournamentOfChampionsStore.shouldRenderDialog,
      tocOwnsSlot: tournamentOfChampionsStore.ownsCurrentTournamentSlot,
    });
  const storeMetagameAvailable = selectStoreMetagameCtaAvailability({
    metagamesEnabled,
    isMetagameAvailable: (kind) => metagamesStore.isMetagameAvailable(kind),
    isCardWarEnded: cardWarStore.isCurrentEventEnded,
  });

  return resolveBannerCtaAction(uiStore.bannerCta, coinType, {
    startGame: (gameId, coin) => startWithPhoneGate(coin, () => handleStartGame(gameId, 'lobby_banner', coin)),
    inviteFriendsEnabled,
    openInviteFriends: () => {
      void rewardsStore.fetchReferralData();
      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenInviteFriendsNew,
      });
    },
    tournamentOfChampionsAvailable: tournamentOfChampionsStore.tournamentOfChampions != null,
    getTournamentCanOpenDialog: () => tournamentOfChampionsStore.canOpenDialog,
    fetchTournament: async () => {
      await tournamentStore.fetch();
    },
    openTournamentDialog: () =>
      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenTournamentDialog,
        options: { shown_by: 'widget_click' },
      }),
    chaseAvailable: isChaseAvailable({
      questsFlag: questStore.questsFlag,
      currentPlayerQuest: questStore.currentPlayerQuest,
    }),
    openChase: () => {
      void questStore.refreshMilestoneProgress();
      uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenQuestDialog });
    },
    tournamentLeaderboardAvailable: getTournamentLeaderboardAvailable(),
    getTournamentLeaderboardAvailable,
    crownSportsFeatureEnabled: isCrownSportsEnabled,
    sportEventAvailable: monetizationEventsEnabled && sportEventsStore.sportEvent != null,
    startSportEvent: () => void sportEventsStore.startSportEvent(),
    crownJackpotAvailable: jackpotStore.canShowWidget,
    openCrownJackpot: () => gameUiStore.openJackpotDialog({ source: 'header_banner' }),
    benefitEventAvailable: (benefitKey) =>
      eventTriggerWidgets.some((entry) => entry.data.event.benefit.key === benefitKey),
    openBenefitEvent: (benefitKey) => {
      const found = eventTriggerWidgets.find((entry) => entry.data.event.benefit.key === benefitKey);
      if (found) void eventTriggerStore.openPopupByEventTriggerId(found.data.event.eventTrigger.eventTriggerId);
    },
    metagameAvailable: {
      hammers: metagamesEnabled && hammersStore.hammersEventByCoin != null,
      trail: metagamesEnabled && trailStore.trailEventByCoin != null,
      puzzle: metagamesEnabled && puzzleStore.puzzleEventByCoin != null,
      ...storeMetagameAvailable,
    },
    startMetagame: {
      hammers: () => void hammersStore.startHammersGame(),
      trail: () => void trailStore.startTrailGame(),
      puzzle: () => void puzzleStore.startPuzzleGame(),
      prizeDrop: () => void metagamesStore.startMetagameByKind(MetaGamesEventType.PRIZE_DROP, noop),
      cardWar: () => void metagamesStore.startMetagameByKind(MetaGamesEventType.CARD_WAR, noop),
    },
  });
};
